/**
 * LogoSpinner — animated Workpay diamond logo.
 *
 * The shared visual primitive behind GlobalLoader and PageLoader. Renders the
 * brand diamond as two stacked SVG paths: a muted base fill (always visible)
 * and a stroked overlay that traces the perimeter on a loop, inside a wrapper
 * that runs a subtle scale "breathe" pulse. Both animations are disabled under
 * `prefers-reduced-motion`.
 *
 * Visual spec source: UX reference SubmittingPage.jsx (Workpay diamond mark).
 */
import styles from './LogoSpinner.module.scss';

// ============================================
// TYPES
// ============================================

export type LogoSpinnerSize = 'sm' | 'md' | 'lg';

export interface LogoSpinnerProps {
  /** Rendered size of the logo. @default 'md' */
  size?: LogoSpinnerSize;
  /** Additional CSS class applied to the wrapper. */
  className?: string;
}

// ============================================
// CONSTANTS
// ============================================

/** Workpay diamond mark — viewBox "0 0 32.5 24". Copied from the UX reference file. */
const WORKPAY_DIAMOND_PATH =
  'M31.4595 7.93514L28.0951 4.57069C27.7725 4.2447 27.3885 3.98591 26.9653 3.8093C26.5421 3.63268 26.088 3.54174 25.6295 3.54174C25.1709 3.54174 24.7168 3.63268 24.2936 3.8093C23.8704 3.98591 23.4864 4.2447 23.1638 4.57069L16.2385 11.4816L9.31384 4.56996C8.99128 4.24398 8.60728 3.98519 8.18406 3.80857C7.76084 3.63195 7.30679 3.54102 6.8482 3.54102C6.3896 3.54102 5.93556 3.63195 5.51233 3.80857C5.08911 3.98519 4.70511 4.24398 4.38256 4.56996L1.0181 7.93442C0.366067 8.58935 0 9.47589 0 10.4001C0 11.3242 0.366067 12.2108 1.0181 12.8657L7.48907 19.3377C8.144 19.9898 9.03054 20.3559 9.95471 20.3559C10.8789 20.3559 11.7654 19.9898 12.4204 19.3377L15.5269 16.2312L16.1871 15.571L18.2311 13.5266L25.1564 6.61505C25.2681 6.50357 25.4194 6.44096 25.5772 6.44096C25.7349 6.44096 25.8863 6.50357 25.9979 6.61505L29.3624 9.9795C29.4738 10.0911 29.5365 10.2425 29.5365 10.4002C29.5365 10.558 29.4738 10.7093 29.3624 10.821L22.8914 17.2934C22.7798 17.4049 22.6284 17.4675 22.4707 17.4675C22.3129 17.4675 22.1616 17.4049 22.0499 17.2934L18.2575 13.501V17.5908L20.0302 19.4159C20.6851 20.0679 21.5716 20.434 22.4958 20.434C23.42 20.434 24.3065 20.0679 24.9614 19.4159L31.4339 12.9442C31.7702 12.6203 32.0382 12.2323 32.2221 11.803C32.4059 11.3738 32.5019 10.9121 32.5043 10.4452C32.5068 9.97823 32.4156 9.51554 32.2362 9.08443C32.0568 8.65332 31.7929 8.26253 31.4599 7.93514H31.4595ZM10.3492 17.3191C10.2376 17.4305 10.0863 17.4932 9.92848 17.4932C9.77071 17.4932 9.61939 17.4305 9.50774 17.3191L3.03678 10.8474C2.9253 10.7357 2.86269 10.5844 2.86269 10.4266C2.86269 10.2689 2.9253 10.1176 3.03678 10.0059L6.46671 6.61468C6.57836 6.50321 6.72968 6.4406 6.88745 6.4406C7.04522 6.4406 7.19654 6.50321 7.30819 6.61468L14.2325 13.5266L10.3492 17.3191Z';

// ============================================
// COMPONENT
// ============================================

function LogoSpinner({ size = 'md', className }: LogoSpinnerProps) {
  const wrapperClasses = [styles.iconWrap, styles[size], className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={wrapperClasses} aria-hidden="true">
      <svg viewBox="0 0 32.5 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d={WORKPAY_DIAMOND_PATH} className={styles.base} />
        <path d={WORKPAY_DIAMOND_PATH} className={styles.trace} />
      </svg>
    </div>
  );
}

export default LogoSpinner;
